import { useEffect, useRef } from 'react';

import { useWebSocket } from './WebSocketProvider.jsx';

// Same deep-relative path the WebSocketProvider uses: the socket client (and its
// States enum) is reused in place from the mobile tree, not copied.
import { States } from '../../../app/src/components/WebSocketClient.js';

// A slim, unobtrusive status bar shown at the top of the dashboard while the live
// WebSocket connection is not healthy. The store is still readable from its last
// seeded/live values underneath, so this only tells the user their view may be
// going stale — it never blocks the dashboard.
//
// Status is conveyed by TEXT, not colour alone (WCAG 1.4.1), and each visible
// state is a polite live region so a screen reader hears the transition.
export default function ConnectionBanner() {
  const ws = useWebSocket();
  const status = ws?.webSocketInfo?.webSocketStatus;

  // Latch whether the socket has ever been Open. On a fresh load the client goes
  // Initial -> AttemptingReconnect('Reconnecting') -> Open, so without this the
  // banner would briefly say "Reconnecting…" during the very first connect —
  // misleading, since nothing has dropped yet. Only surface the reconnect states
  // once a healthy connection has actually been seen. (A first connect that never
  // opens still surfaces via the Closed branch below.)
  const hasBeenOpenRef = useRef(false);
  useEffect(() => {
    if (status === States.Open) {
      hasBeenOpenRef.current = true;
    }
  }, [status]);

  // Defensive: safe to mount even outside the WebSocket provider (useWebSocket
  // returns null there) — render nothing rather than throw.
  if (!ws) {
    return null;
  }

  const { resetRetries } = ws;

  switch (status) {
    // Healthy, not-yet-opened, or deliberately disabled: nothing to say.
    case States.Open:
    case States.Initial:
    case States.Disabled:
      return null;

    // Both reconnect states (AttemptingReconnect + Reconnecting/"Waiting") mean
    // the client is still trying on its own — no user action needed. Suppressed
    // until the socket has opened at least once (see hasBeenOpenRef).
    case States.AttemptingReconnect:
    case States.Reconnecting:
      if (!hasBeenOpenRef.current) {
        return null;
      }
      return (
        <div
          className="connection-banner connection-banner--reconnecting"
          role="status"
          aria-live="polite"
        >
          Reconnecting…
        </div>
      );

    // Retries exhausted and the client has given up — it will NOT recover on its
    // own, so the message must (a) make clear it is the user's own connection that
    // dropped, not their heating, and (b) offer the manual retry. Phrasing avoids
    // jargon for non-technical users: "you appear to be offline" points at their
    // device, and "reconnect to control your heating" implies the heating itself
    // is unaffected.
    case States.Closed:
      return (
        <div
          className="connection-banner connection-banner--closed"
          role="status"
          aria-live="polite"
        >
          <span>
            You appear to be offline — reconnect to control your heating.
          </span>
          <button
            type="button"
            className="connection-banner__retry"
            onClick={resetRetries}
          >
            Try again
          </button>
        </div>
      );

    default:
      return null;
  }
}
